// src/services/itemService.js
// P4-2 (30 Oct 2026, D-319 decision 3) — the ONLY writer of new Item Master items.
//
// WHY THIS FILE EXISTS
// Item Master, Item Requests and the BOQ upload each created items their own
// way: a code built from the first words of the name (dev: 451 of 646 items
// share a code), two separate writes (an item could exist without its stock
// doc), and three different duplicate checks (BOQ had none). Now:
//   - item_code = category prefix + the number part of item_id
//     (ITEM-100123 -> MT-100123; legacy ITEM-00042 -> MT-00042) — unique
//     because item_id is unique (reserveItemId(), D-300); never typed, never edited
//   - items + inventory_summary/{item_id} written in ONE batch
//   - a name whose itemNameKey() matches an ACTIVE item of the company or a
//     shared "" item is refused ("250 mm" = "250mm") — on create AND on edit
//   - item_name_lower written (the MRF / StoreMRFDetail duplicate queries read it)
// Rules (D-320): items create + edit = owner / sm / manager / store of the company;
// disable = owner / sm only, ITEM_DISABLE_ROLES below; a manager / store edit is
// pinned to the fields updateItem() writes. inventory_summary create = own company.
// Tests: itemService.test.js (Jest), tests/rules/d320.rules.test.mjs (emulator).

import {
  collection, doc, getDoc, getDocs, query, where, writeBatch, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { reserveItemId } from "./itemIdService";
import { itemNameKey } from "../utils/itemMatch";
import { checkHsn } from "../utils/gstBill";
import { itemCodeFor } from "../utils/itemCode";

export { CATEGORY_PREFIX, itemCodeFor } from "../utils/itemCode";
export const DUPLICATE_ITEM = "duplicate_item";

// D-320: the same list as the items update rule (isOwnerOrSM); change both together.
export const ITEM_DISABLE_ROLES = ["owner", "sm"];
export const canDisableItem = (role) => ITEM_DISABLE_ROLES.includes(role);

const clean = (v) => String(v ?? "").trim();

// Pure: the ACTIVE items with the same name key (inactive items never block).
export const sameNameItems = (items, name, { exceptDocId = "" } = {}) => {
  const key = itemNameKey(name);
  if (!key) return [];
  return (Array.isArray(items) ? items : []).filter((it) =>
    it && it.is_active !== false && it.id !== exceptDocId && itemNameKey(it.item_name) === key);
};

const duplicateError = (name, hits) => {
  const list = hits.map((h) => `${clean(h.item_code) || clean(h.item_id)} (${clean(h.item_name)})`).join(", ");
  const err = new Error(`"${clean(name)}" already exists in Item Master as ${list}. Use the existing item instead.`);
  err.code = DUPLICATE_ITEM;
  err.existing = hits;
  return err;
};

// Fresh read right before the write: own company + shared "" items.
const companyItems = async (company_id) => {
  const snap = await getDocs(query(collection(db, "items"), where("company_id", "in", [company_id, ""])));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

const hsnOrThrow = (hsn) => {
  const r = checkHsn(hsn);
  if (!r.ok) throw new Error(r.reason);
  return r.hsn;
};
const gstOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

/**
 * Creates one item + its inventory_summary/{item_id} doc in ONE batch.
 * @returns {Promise<{ docId, item_id, item_code, item_name, specification, unit, category }>}
 * Throws err.code === DUPLICATE_ITEM (err.existing = the matching items) when the name exists.
 */
export async function createItem({
  company_id, item_id, item_name, unit, category = "MATERIAL",
  specification = "", minimum_stock = 0, preferred_vendor_id = "", preferred_vendor_name = "",
  hsn_code = "", gst_percent = null, description, source, original_request,
} = {}) {
  const cid  = clean(company_id);
  const name = clean(item_name);
  if (!cid)          throw new Error("company_id required");
  if (!name)         throw new Error("Item name is required.");
  if (!clean(unit))  throw new Error("Unit is required.");
  const hsn = hsnOrThrow(hsn_code);

  const hits = sameNameItems(await companyItems(cid), name);
  if (hits.length > 0) throw duplicateError(name, hits);

  const id = item_id === undefined || item_id === null || item_id === "" ? await reserveItemId() : clean(item_id);
  const item_code = itemCodeFor(category, id);   // throws on a bad id
  const spec = clean(specification);
  const minStock = Number(minimum_stock || 0);

  const itemRef = doc(collection(db, "items"));
  const batch = writeBatch(db);
  batch.set(itemRef, {
    item_id:               id,
    item_code,
    item_name:             name,
    item_name_lower:       name.toLowerCase(),
    specification:         spec,
    unit,
    category,
    minimum_stock:         minStock,
    preferred_vendor_id:   preferred_vendor_id   || "",
    preferred_vendor_name: preferred_vendor_name || "",
    hsn_code:              hsn,
    gst_percent:           gstOrNull(gst_percent),
    company_id:            cid,
    is_active:             true,
    ...(description      !== undefined ? { description }      : {}),
    ...(source           !== undefined ? { source }           : {}),
    ...(original_request !== undefined ? { original_request } : {}),
    created_at:            serverTimestamp(),
    updated_at:            serverTimestamp(),
  });
  batch.set(doc(db, "inventory_summary", id), {
    company_id:         cid,
    item_id:            id,
    item_master_doc_id: itemRef.id,
    item_name:          name,
    specification:      spec,
    item_code,
    category,
    unit,
    qty:                0,
    defective_stock:    0,
    scrap_stock:        0,
    minimum_stock:      minStock,
    is_active:          true,
    created_at:         serverTimestamp(),
    updated_at:         serverTimestamp(),
  });
  await batch.commit();

  return { docId: itemRef.id, item_id: id, item_code, item_name: name, specification: spec, unit, category };
}

// Reads one item and checks it is the caller company's own (shared "" items
// are read-only here — the rules refuse their update too).
const ownItem = async (company_id, docId) => {
  const cid = clean(company_id);
  if (!cid || !clean(docId)) throw new Error("company_id and item required");
  const snap = await getDoc(doc(db, "items", clean(docId)));
  if (!snap.exists()) throw new Error("Item not found.");
  const item = { id: snap.id, ...snap.data() };
  if (item.company_id === "") throw new Error("This is a shared item — it cannot be edited here.");
  if (item.company_id !== cid) throw new Error("This item belongs to another company.");
  return item;
};

/**
 * Edits an item (never its item_code or item_id) and syncs its own stock doc, in ONE batch.
 * @returns {Promise<{ docId, item_id, item_code }>}
 */
export async function updateItem({ company_id, docId, changes = {} } = {}) {
  const item = await ownItem(company_id, docId);
  const name = clean(changes.item_name ?? item.item_name);
  if (!name) throw new Error("Item name is required.");

  const hits = sameNameItems(await companyItems(clean(company_id)), name, { exceptDocId: item.id });
  if (hits.length > 0) throw duplicateError(name, hits);

  const fields = {
    item_name:             name,
    item_name_lower:       name.toLowerCase(),
    specification:         clean(changes.specification ?? item.specification),
    unit:                  changes.unit ?? item.unit ?? "",
    category:              changes.category ?? item.category ?? "",
    minimum_stock:         Number((changes.minimum_stock ?? item.minimum_stock) || 0),
    preferred_vendor_id:   (changes.preferred_vendor_id   ?? item.preferred_vendor_id)   || "",
    preferred_vendor_name: (changes.preferred_vendor_name ?? item.preferred_vendor_name) || "",
    hsn_code:              hsnOrThrow(changes.hsn_code ?? item.hsn_code),
    gst_percent:           gstOrNull(changes.gst_percent !== undefined ? changes.gst_percent : item.gst_percent),
    updated_at:            serverTimestamp(),
  };

  const batch = writeBatch(db);
  batch.update(doc(db, "items", item.id), fields);
  if (clean(item.item_id)) {
    const sumRef = doc(db, "inventory_summary", clean(item.item_id));
    // A legacy stock doc with company_id "" (or none) is not readable by the
    // company (read rule = sameCompany) — skip it and still save the item,
    // exactly as before P4-2 (the old summary update failed and was ignored).
    let sumSnap = null;
    try { sumSnap = await getDoc(sumRef); } catch { sumSnap = null; }
    if (sumSnap?.exists() && sumSnap.data().company_id === clean(company_id)) {
      batch.update(sumRef, {
        item_name:             fields.item_name,
        specification:         fields.specification,
        unit:                  fields.unit,
        category:              fields.category,
        preferred_vendor_id:   fields.preferred_vendor_id,
        preferred_vendor_name: fields.preferred_vendor_name,
        updated_at:            serverTimestamp(),
      });
    }
  }
  await batch.commit();
  return { docId: item.id, item_id: item.item_id, item_code: item.item_code };
}

/** Disables an item (is_active: false) — items are never deleted. @returns {Promise<void>} */
export async function disableItem({ company_id, docId } = {}) {
  const item = await ownItem(company_id, docId);
  const batch = writeBatch(db);
  batch.update(doc(db, "items", item.id), { is_active: false, updated_at: serverTimestamp() });
  await batch.commit();
}
